import { useCallback, useState } from "react";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";
import { formatBitmartSymbol, useBitmartWsConnection } from "@/hooks/exchanges/direct-ws/bitmart";
import type { BitmartOrderBookMessage } from "@/hooks/exchanges/direct-ws/bitmart/types";

const EXCHANGE_ID = "bitmart";
const DEPTH_CHANNEL = "spot/depth20";

/**
 * Hook for subscribing to BitMart spot orderbook updates via shared WebSocket.
 * Uses the public depth-all channel which pushes the full top-20 snapshot on every update.
 * See: https://developer-pro.bitmart.com/en/spot/#public-depth-all-channel
 */
export const useBitmartOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [orderBookError, setOrderBookError] = useState<string | null>(null);

  const setup = useCallback(
    (ws: WebSocket) => {
      if (!pairSymbol) return;

      const symbol = formatBitmartSymbol(pairSymbol);
      const channel = `${DEPTH_CHANNEL}:${symbol}`;

      ws.send(JSON.stringify({ op: "subscribe", args: [channel] }));

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ op: "unsubscribe", args: [channel] }));
        }
      };
    },
    [pairSymbol],
  );

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!pairSymbol) return;
      const expectedSymbol = formatBitmartSymbol(pairSymbol);

      try {
        const typed = msg as BitmartOrderBookMessage;

        if (typed.table === DEPTH_CHANNEL && typed.data?.[0]?.symbol === expectedSymbol) {
          const data = typed.data[0];
          const rawBids = data.bids;
          const rawAsks = data.asks;

          if (rawBids || rawAsks) {
            const bids: [number, number][] = (rawBids || [])
              .slice(0, 20)
              .map(([price, size]) => [parseFloat(price), parseFloat(size)]);

            const asks: [number, number][] = (rawAsks || [])
              .slice(0, 20)
              .map(([price, size]) => [parseFloat(price), parseFloat(size)]);

            setOrderBookData({
              timestamp: data.ms_t ?? Date.now(),
              exchangeId: EXCHANGE_ID,
              pairSymbol,
              bids,
              asks,
            });
            setOrderBookError(null);
          }
        }
      } catch {
        setOrderBookError("Failed to parse orderbook update");
      }
    },
    [pairSymbol],
  );

  const { isConnected, error: connectionError } = useBitmartWsConnection({
    setup,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error: orderBookError ?? connectionError,
  };
};
